import { Button, Intent } from "@blueprintjs/core";
import _ from "lodash";
import React from "react";
import { jsonStringify, reformId } from "../../api/helper";
import { UserLanguage } from "../../lang/lang";
import { ActionStringCollection } from "../../lang/types";
import { gen_id } from "../../utils/id";
import { parseError } from "../Error";
import {
  ConvertInputForSubmit,
  InputBuilderSingle,
  InputCardBuilder,
  InputProperties as InputFieldProperties,
} from "./Form";
import {
  InputFormMode,
  InputFormState,
  isDifferent,
  Mutation,
  ValuesVariables,
} from "./InputForm";

export type KeydDictionary<T, K> = {
  [key in keyof T]: K;
};

export type OptionalKeydDictionary<T, K> = {
  [key in keyof T]?: K;
};

export type Nullable<T> = {
  [P in keyof T]: T[P] | null;
};

export interface InputFormBuilderState<ObjectType> {
  buildCards: () => React.ReactNode;
  buildField: (field: keyof ObjectType) => React.ReactNode;
  buildSubmitButton: () => JSX.Element;
  fields: Partial<ObjectType>;
  submitting: boolean;
}

export type FormInputFields<ObjectType> = {
  [K in keyof ObjectType]?: InputFieldProperties<ObjectType, ObjectType[K]>;
};

export interface BuilderChildren<ObjectType> {
  children: (builder: InputFormBuilderState<ObjectType>) => React.ReactNode;
}

export interface InputFormBuilderProperties<
  ObjectType,
  MutationResponse,
  MutationVariable = any
> extends BuilderChildren<ObjectType> {
  inline?: boolean;
  keys: FormInputFields<ObjectType>;
  // successText: string;
  texts: ActionStringCollection;
  reformId?: (keyof ObjectType)[];
  stringyfy?: (keyof ObjectType)[];
  beforeMutate?: (
    mutation_data: Partial<MutationVariable>,
    keys: FormInputFields<ObjectType>,
    root_object: Partial<ObjectType>
  ) => Partial<MutationVariable>;
  mutation: Mutation<MutationResponse, ValuesVariables<MutationVariable>>;
  onSuccess: (data: MutationResponse) => void;
  onStateChange?: (
    newState: InputFormState<ObjectType>,
    oldState: InputFormState<ObjectType>,
    isKeyDifferent: (key: keyof ObjectType) => boolean
  ) => InputFormState<ObjectType>;
  input?: ObjectType;
  mode: InputFormMode;
  className?: string;
  changes?: (v: boolean) => void;
}

const initial_value = <ObjectType, MutationResponse, MutationVariable>(
  props: InputFormBuilderProperties<
    ObjectType,
    MutationResponse,
    MutationVariable
  >,
  key: keyof ObjectType
): any => {
  const property = _.get(props.keys, key);

  if (property === undefined || property === null) return;

  if (props.input) return _.get(props.input, key);

  if (property.defaultValue) return property.defaultValue;

  return null;
};

export const has_value_changed = <
  ObjectType,
  MutationResponse,
  MutationVariable
>(
  props: InputFormBuilderProperties<
    ObjectType,
    MutationResponse,
    MutationVariable
  >,
  value: any,
  key: keyof ObjectType
) => (value === undefined ? false : initial_value(props, key) !== value);

export const update_form_state = <
  ObjectType,
  MutationResponse,
  MutationVariable
>(
  props: InputFormBuilderProperties<
    ObjectType,
    MutationResponse,
    MutationVariable
  >,
  state?: InputFormState<ObjectType>
): InputFormState<ObjectType> => {
  const force_update = !_.isEqual(props.input, state?.original_input);

  // console.log("update form state", { props, state });
  // console.log({
  //   prop_input: props.input,
  //   original_input: state?.original_input,
  //   eq: force_update,
  // });

  const s: InputFormState<ObjectType> = {
    random_id: state && !force_update ? state.random_id : gen_id(),
    values: _.transform<FormInputFields<ObjectType>, Partial<ObjectType>>(
      props.keys,
      (result, property, key) => {
        const v = initial_value(props, key);
        if (state && !force_update) {
          const cv = _.get(state.values, key);
          if (cv !== v) return _.set(result, key, cv);
        }
        // console.log("setting", { property, key, v });
        if (v !== null && v !== undefined) return _.set(result, key, v);
      }
    ),
    submitting: false,
    success: false,
    error: null,
    scratch_pad: {},
    original_input: props.input,
    changes: props.changes,
    changed: force_update
      ? false
      : _.find(state?.values, (value, key) => {
          const v = has_value_changed(props, value, key as keyof ObjectType);
          // console.log("update_state > change >", { key, value, v });
          return v;
        }) !== undefined,
  };
  // console.log("state", s);
  return s;
};

// const compare = (a: any, b: any) => {
//   return _.reduce(
//     a,
//     (result, value, key) => {
//       if (_.isPlainObject(value)) _.set(result, key, compare(value, b[key]));
//       else if (!_.isEqual(value, b[key])) _.set(result, key, value);

//       return result;
//     },
//     {}
//   );
// };

// ObjectType is the original complete data type
// MutationResponse is the response type of the mutation
// MutationVariable is the variable type of the values object from "values" of the gql query
export class InputFormBuilder<
  ObjectType = any,
  MutationResponse = any,
  MutationVariable = any
> extends React.PureComponent<
  InputFormBuilderProperties<ObjectType, MutationResponse, MutationVariable>,
  InputFormState<ObjectType>
> {
  state = update_form_state(this.props);

  public componentDidUpdate(
    prevProps: Readonly<
      InputFormBuilderProperties<ObjectType, MutationResponse, MutationVariable>
    >
  ) {
    if (isDifferent(this.props, prevProps))
      this.setState(update_form_state(this.props, this.state));
  }

  private onSubmit = () => {
    this.setState({ submitting: true });

    console.log("values before convert", this.state.values);

    let values = ConvertInputForSubmit<ObjectType, MutationVariable>(
      this.props.keys,
      this.state.values
    );

    console.log("values after convert", values);

    if (this.props.beforeMutate) {
      values = this.props.beforeMutate(
        values,
        this.props.keys,
        this.state.values
      );
      console.log("values after beforeMutate", values);
    }

    values = reformId<Partial<ObjectType>, Partial<MutationVariable>>(
      _.pickBy(values, (v) => v !== undefined) as Partial<ObjectType>,
      this.props.reformId || []
    );

    console.log("values after reform", values);

    values = jsonStringify<Partial<ObjectType>, Partial<MutationVariable>>(
      _.pickBy(values, (v) => v !== undefined) as Partial<ObjectType>,
      this.props.stringyfy || []
    );

    console.log("values after stringify", values);

    _.unset(values, "created_at");
    _.unset(values, "updated_at");

    if (this.props.mode === InputFormMode.Create) _.unset(values, "id");

    const data: ValuesVariables<MutationVariable> = {
      values,
    };

    console.log("mutation variables", data);

    this.props
      .mutation({ variables: data })
      .then((v) => {
        this.setState({ success: true, submitting: true });
        this.props.changes?.(false);
        console.log("success?", v);
        setTimeout(() => {
          this.props.onSuccess(v.data!);
          this.setState(update_form_state(this.props));
        }, 500);
      })
      .catch((e) => {
        console.log("error", e);
        this.setState({
          success: false,
          submitting: false,
          error: parseError(e),
        });
      });
  };

  update_state(new_state: InputFormState<ObjectType>) {
    const changed =
      _.find(new_state.values, (value, key) => {
        const v = has_value_changed(this.props, value, key as keyof ObjectType);
        // console.log("update_state > change >", { key, value, v });
        return v;
      }) !== undefined;

    let set_state = new_state;

    if (this.props.onStateChange)
      set_state = this.props.onStateChange(new_state, this.state, (k) =>
        has_value_changed(this.props, _.get(new_state.values, k), k)
      );

    _.set(set_state, "changed", changed);

    this.setState(set_state);

    this.props.changes?.(changed);
  }

  render = () => {
    const buildCards = () => {
      return InputCardBuilder({
        keys: this.props.keys,
        set_state: (s) => this.update_state(s),
        state: this.state,
        success_text:
          this.props.mode === InputFormMode.Create
            ? this.props.texts.create.success
            : this.props.texts.update.success,
        root: this.state.values,
        changed: false,
      });
    };

    const buildField = (field: keyof ObjectType) => {
      if (!field) return null;

      // const ch = has_value_changed(
      //   this.props,
      //   _.get(this.state.values, field),
      //   field
      // );

      // console.log("building field", field, { state: this.state });

      return InputBuilderSingle({
        field: this.props.keys[field],
        index: field.toString(),
        set_state: (s) => this.update_state(s),
        state: this.state,
        root: this.state.values,
        changed: has_value_changed(
          this.props,
          _.get(this.state.values, field),
          field
        ),
      });
    };

    const buildSubmitButton = () => {
      // console.log("building submit button", { state: this.state });

      return (
        <Button
          disabled={this.state.submitting || !this.state.changed}
          intent={Intent.PRIMARY}
          onClick={() => this.onSubmit()}
          icon={
            this.props.mode === InputFormMode.Create ? "plus" : "floppy-disk"
          }
        >
          {this.props.mode === InputFormMode.Create
            ? UserLanguage().actions.create
            : UserLanguage().actions.save}
        </Button>
      );
    };

    return (
      <div className={this.props.className}>
        {this.props.children({
          buildCards,
          buildField,
          buildSubmitButton,
          submitting: this.state.submitting,
          fields: this.state.values,
        })}
      </div>
    );
  };
}
